import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector from 'file-browser/FolderSelector';
import {formatFileSize, validateFileForUpload} from 'files/fileTypeValidation';
import {createFilesFromUpload} from 'files/network/FileDAO';
import FileStates from 'FileStates';
import notifications from 'notifications';
import {FONT_PROXIMA_14} from 'styles/FontConstants';
import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import {SemanticColors} from 'styles/Utilities';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import * as Colors from 'ui-library/Colors';
import {DYNAMIC_LAYOUT_SPACING} from 'ui-library/DynamicLayout';
import FileChooser from 'ui-library/FileChooser';
import Modal from 'ui-library/modals/Modal';
import {upload} from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

interface Props {
  onRequestClose: () => void;
  initialFolderId?: string;
}

enum UploadStage {
  IDLE = 'IDLE',
  UPLOADING = 'UPLOADING',
  CREATING_FILE = 'CREATING_FILE',
}

interface State {
  folderId: string | null;
  selectedFiles: Array<File>;
  fileErrors: Array<string>;
  uploadStage: UploadStage;
}

class CreateFileModal extends React.Component<Props, State> {
  _isMounted?: boolean;

  override state: State = {
    folderId: this.props.initialFolderId ?? null,
    selectedFiles: [],
    fileErrors: [],
    uploadStage: UploadStage.IDLE,
  };

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  handleFilesSelected = (...files: Array<File>): void => {
    const validFiles: Array<File> = [];
    const errors: Array<string> = [];

    for (const file of files) {
      const validationError = validateFileForUpload(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        validFiles.push(file);
      }
    }

    this.setState({selectedFiles: validFiles, fileErrors: errors});
  };

  handleSubmit = async (): Promise<void> => {
    const {selectedFiles, folderId} = this.state;
    if (selectedFiles.length === 0 || folderId == null) {
      return;
    }

    try {
      // Phase 1: Upload all files to S3 via ImportUtil.upload()
      this.setState({uploadStage: UploadStage.UPLOADING});
      // Wrap in Promise.resolve() because ImportUtil.upload returns a jQuery Deferred
      const uploadResults = await Promise.allSettled(
        selectedFiles.map((file) => Promise.resolve(upload(file)))
      );

      const succeeded: Array<{file: File; s3Location: string}> = [];
      const failedNames: Array<string> = [];
      uploadResults.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          succeeded.push({file: selectedFiles[i], s3Location: result.value!});
        } else {
          failedNames.push(selectedFiles[i].name);
        }
      });

      if (succeeded.length === 0) {
        throw new Error('All file uploads failed');
      }

      if (failedNames.length > 0) {
        notifications.error(`Failed to upload: ${failedNames.join(', ')}`);
      }

      // Phase 2: Create file records in a single transactional request
      this.setState({uploadStage: UploadStage.CREATING_FILE});
      const response = await createFilesFromUpload(
        succeeded.map(({file, s3Location}) => ({
          name: file.name,
          filename: file.name,
          folderId,
          s3Location,
        }))
      );

      FolderDAOCache.invalidateFolderContents(folderId);
      if (response.createdIds.length === 1) {
        FileStates.get().openFolderItem(response.createdIds[0]);
      }
      this.props.onRequestClose();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (err) {
      const errorMessage =
        getUserMessage(err) ?? (err instanceof Error ? err.message : null) ?? 'An unexpected error occurred';
      notifications.error(errorMessage);
      if (this._isMounted) {
        this.setState({uploadStage: UploadStage.IDLE});
      }
    }
  };

  isSubmitEnabled = (): boolean => {
    const {selectedFiles, folderId, uploadStage, fileErrors} = this.state;
    return (
      selectedFiles.length > 0 &&
      folderId != null &&
      uploadStage === UploadStage.IDLE &&
      fileErrors.length === 0
    );
  };

  getUploadStatusMessage = (): string | null => {
    const {uploadStage} = this.state;
    switch (uploadStage) {
      case UploadStage.UPLOADING:
        return 'Uploading files...';
      case UploadStage.CREATING_FILE:
        return 'Creating file records...';
      default:
        return null;
    }
  };

  override render(): JSX.Element {
    const {folderId, selectedFiles, fileErrors, uploadStage} = this.state;
    const showFolderSelector = this.props.initialFolderId == null;
    const isUploading = uploadStage !== UploadStage.IDLE;
    const uploadStatusMessage = this.getUploadStatusMessage();
    const hasErrors = fileErrors.length > 0;

    return (
      <Modal
        title="Create file"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleSubmit}
        buttons={[{text: 'Create', submit: true, disabled: !this.isSubmitEnabled()}]}
        analyticsModule={ANALYTICS_MODULE.PROJECT_FOLDER}
        analyticsModalType="create_file"
      >
        {showFolderSelector && (
          <FormGroup>
            <Label className="required">Location</Label>
            <FolderSelector
              enableRedesign={true}
              level="append"
              showInventory={true}
              showNotebook={true}
              value={folderId}
              onChange={(e) => {
                this.setState({folderId: e.target.value});
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        )}
        <FormGroup hasError={hasErrors}>
          <Label className="required">File</Label>
          <FileChooser multiple={true} onSelectFiles={this.handleFilesSelected} />
          {selectedFiles.length > 0 && !hasErrors && (
            <div id="file-info">
              {selectedFiles.map((file, i) => (
                <p key={i} className={css(styles.selectedFileName)}>
                  {file.name} ({formatFileSize(file.size)})
                </p>
              ))}
            </div>
          )}
          {hasErrors &&
            fileErrors.map((error, i) => (
              <p key={i} className={css(styles.errorText)} role="alert">
                {error}
              </p>
            ))}
        </FormGroup>
        {isUploading && uploadStatusMessage && (
          <div className={css(styles.uploadStatus)} role="status" aria-live="polite">
            {uploadStatusMessage}
          </div>
        )}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  selectedFileName: {
    marginTop: 8,
    marginBottom: 0,
    ...FONT_PROXIMA_14,
  },
  errorText: {
    marginTop: 8,
    marginBottom: 0,
    color: SemanticColors.dangerColor,
  },
  uploadStatus: {
    marginTop: 16,
    padding: DYNAMIC_LAYOUT_SPACING,
    backgroundColor: Colors.GRAY_LIGHTER,
    borderRadius: BORDER_RADIUS_DEFAULT,
    ...FONT_PROXIMA_14,
    textAlign: 'center',
  },
});

export default CreateFileModal;
